import { useEffect, useState } from 'react';
import { ApiError } from '../api/ApiError';
import { DEMO_RIDER, USERNAME_MAX_LENGTH } from '../api/client';
import { errorCopy } from '../api/errorCopy';
import { SOURCE_LABELS, type QueueUpdate, type Source } from '../api/types';
import hubScene from './hubScene';
import { RouteMap } from './RouteMap';
import { Scenery } from './Scenery';
import { Wordmark } from './Wordmark';

interface Props {
  onSubmit: (username: string, source: Source) => void;
  /** Drops a failed attempt, so it doesn't follow the rider to the other choice. */
  onClear: () => void;
  /** Ask first whether to ride their own profile or the demo one (the /demo address). */
  offerDemo: boolean;
  busy: boolean;
  /** Where the request stands in the queue for AniList, when it is in one. */
  queue?: QueueUpdate | null;
  error: ApiError | null;
  /** Kept across a failed attempt so the rider is not retyping their own name. */
  initialUsername: string;
  initialSource: Source;
}

const SOURCES: Source[] = ['mal', 'anilist'];

function placeInLine(position: number): string {
  if (position <= 1) return "YOU'RE NEXT IN LINE";
  const tens = position % 100;
  const suffix =
    tens >= 11 && tens <= 13 ? 'TH' : ({ 1: 'ST', 2: 'ND', 3: 'RD' }[position % 10] ?? 'TH');
  return `YOU'RE ${position}${suffix} IN LINE`;
}

function roughWait(seconds: number): string {
  if (seconds < 60) return `ABOUT ${Math.max(1, seconds)} SEC`;
  return `ABOUT ${Math.ceil(seconds / 60)} MIN`;
}

/**
 * The gate. Nothing about the network exists until a list has been read, so
 * this is the one screen the rider always starts at. It shows the name and a
 * tagline and takes a username, like the title card before an episode,
 * with the map the list will be drawn onto running faintly behind it: every
 * line in the system, before a list decides which of them run. At /demo it asks
 * first whether to ride their own profile or the demo one.
 */
export function Gate({
  onSubmit,
  onClear,
  offerDemo,
  busy,
  queue,
  error,
  initialUsername,
  initialSource,
}: Props) {
  const [choice, setChoice] = useState<'undecided' | 'own'>(offerDemo ? 'undecided' : 'own');
  // Coming back from the username form, focus returns to the choice it left.
  const [cameBack, setCameBack] = useState(false);
  const [username, setUsername] = useState(initialUsername);
  const [source, setSource] = useState<Source>(initialSource);
  const [touched, setTouched] = useState(false);

  useEffect(() => setUsername(initialUsername), [initialUsername]);

  const trimmed = username.trim();
  const empty = trimmed === '';
  const copy = error ? errorCopy(error, source, trimmed) : null;

  return (
    <>
      <Scenery scene={hubScene} />

      <div className="wrap">
        <section className="hero">
          <header className="gatehead">
            <p className="kicker">中央駅 ・ CENTRAL STATION ・ ANIME RECOMMENDER</p>
            <h1 className="mark">
              次{' '}
              <span className="latin">
                {/* spread across the Latin name, so アニ sits over ANI and メトロ over METRO */}
                <span className="kana" aria-hidden="true">
                  {[...'アニ'].map((c) => (
                    <i key={c} className="wm-ani">
                      {c}
                    </i>
                  ))}
                  {[...'メトロ'].map((c) => (
                    <i key={c} className="wm-metro">
                      {c}
                    </i>
                  ))}
                </span>
                <Wordmark />
              </span>
            </h1>
            <p className="lede">Exploring your anime journey</p>
          </header>

          <form
            className={`farecard${busy ? ' busy' : ''}`}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (choice !== 'own') return;
              setTouched(true);
              if (empty || busy) return;
              onSubmit(trimmed, source);
            }}
          >
            {choice === 'undecided' ? (
              <>
                <div className="fc-top">
                  <span>乗車券 ・ CHOOSE YOUR TICKET</span>
                </div>
                <div className="choices" role="group" aria-label="Whose list to ride">
                  <button
                    type="button"
                    className="choice own"
                    disabled={busy}
                    autoFocus={cameBack}
                    onClick={() => {
                      onClear();
                      setUsername('');
                      setChoice('own');
                    }}
                  >
                    <b>乗車券 ・ MY OWN PROFILE</b>
                    <small>MyAnimeList or AniList username</small>
                  </button>
                  <button
                    type="button"
                    className="choice demo"
                    disabled={busy}
                    onClick={() => onSubmit(DEMO_RIDER, source)}
                  >
                    <b>試乗 ・ DEMO PROFILE</b>
                    <small>Try a sample list, no account needed</small>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="fc-top">
                  <span>乗車券 ・ TICKETS</span>
                  <div className="sources" role="group" aria-label="List source">
                    {SOURCES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        aria-pressed={s === source}
                        onClick={() => setSource(s)}
                        disabled={busy}
                      >
                        {SOURCE_LABELS[s].toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="fieldrow">
                  <label className="srOnly" htmlFor="tsugi-username">
                    Your {SOURCE_LABELS[source]} username
                  </label>
                  <input
                    id="tsugi-username"
                    name="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={`${SOURCE_LABELS[source]} username`}
                    maxLength={USERNAME_MAX_LENGTH}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={busy}
                    // This is the only real control on screen,
                    // so focusing it steals nothing from anyone.
                    autoFocus
                  />
                  <button className="issue" type="submit" disabled={busy}>
                    {busy ? '発券中 ・ READING' : '発券 ・ ISSUE TICKETS'}
                  </button>
                </div>

                {offerDemo && (
                  <button
                    type="button"
                    className="back"
                    disabled={busy}
                    onClick={() => {
                      onClear();
                      setTouched(false);
                      setCameBack(true);
                      setChoice('undecided');
                    }}
                  >
                    ← BACK
                  </button>
                )}
              </>
            )}

            <div className="readout" aria-live="polite">
              {busy &&
                (queue?.state === 'queued' ? (
                  <span className="working">
                    待機中 ・ {placeInLine(queue.position)} ・ {roughWait(queue.eta_seconds)}
                  </span>
                ) : choice === 'undecided' ? (
                  <span className="working">試乗 ・ LOADING THE DEMO PROFILE</span>
                ) : (
                  <span className="working">
                    {SOURCE_LABELS[source].toUpperCase()} に接続中 ・ FETCHING {trimmed.toUpperCase()}
                  </span>
                ))}
              {!busy && copy && (
                <>
                  <span className="bad">運転見合わせ ・ {copy.title.toUpperCase()}</span>
                  <span className="hint">{copy.body}</span>
                  {copy.hint && <span className="hint">{copy.hint}</span>}
                  {copy.retryable && !empty && (
                    <button
                      type="button"
                      className="retry"
                      onClick={() => onSubmit(trimmed, source)}
                    >
                      再試行 ・ TRY AGAIN
                    </button>
                  )}
                </>
              )}
              {!busy && !copy && touched && empty && (
                <span>氏名未入力 ・ ENTER A USERNAME TO CONTINUE</span>
              )}
            </div>
          </form>
        </section>

        <RouteMap busy={busy} />
      </div>

      <footer className="privacy">
        <a href="/disclaimer.html">免責事項 ・ DISCLAIMER</a>
      </footer>
    </>
  );
}
